import React, { FC, useEffect, useState } from 'react'
import { jsPDF } from "jspdf"
import { domToCanvas } from 'modern-screenshot'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingSquare from '../common/LoadingSquare'

interface Props {
  title: string
  target: string
  beforeFunc?: () => void
  afterFunc?: () => void
}

const PdfModule: FC<Props> = (props) => {
  const {
    title,
    target,
    beforeFunc,
    afterFunc,
  } = props

  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false)

  // Canvasの切り抜き
  const clipCanvas = (
    baseCanvas: HTMLCanvasElement,
    canvasPdfY: number,
    pdfWidth: number,
    clipPdfHeight: number,
  ) => {
    const tmpClipCanvas = document.createElement('canvas')

    // PDFの高さからCanvasの高さを逆算
    const clipCanvasHeight = (pdfHeight: number) => (pdfHeight * baseCanvas.width) / pdfWidth

    tmpClipCanvas.width = baseCanvas.width
    tmpClipCanvas.height = clipCanvasHeight(clipPdfHeight)
    const context = tmpClipCanvas.getContext('2d')
    if (context) {
      context.drawImage(baseCanvas, 0, clipCanvasHeight(canvasPdfY))
    }

    return {
      height: tmpClipCanvas.height,
      width: tmpClipCanvas.width,
      dataUrl: tmpClipCanvas.toDataURL('image/png'),
    }
  }

  // 縦横の比率を維持したままPDFサイズに変換
  const convertCanvasHeightForPdf = (
    canvasHeight: number,
    canvasWidth: number,
    pdfContentWidth: number,
  ) => (canvasHeight * pdfContentWidth) / canvasWidth

  const generatePdf = async (element: HTMLElement) => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('p', 'mm', 'a4', true)

    // A4サイズのPDFの高さ・横幅
    const pdfWidth = doc.internal.pageSize.getWidth()
    const pdfHeight = doc.internal.pageSize.getHeight()

    const margin = 10
    const pdfContentWidth = pdfWidth - 2 * margin

    // ElementをCanvas化
    const canvas = await domToCanvas(element)

    const heightInCanvasPdf = convertCanvasHeightForPdf(canvas.height, canvas.width, pdfContentWidth)
    let restImageHeight = heightInCanvasPdf

    while (restImageHeight > 0) {
      if (restImageHeight <= pdfHeight) {
        const clippedCanvas = clipCanvas(
          canvas,
          -(heightInCanvasPdf - restImageHeight),
          pdfContentWidth,
          restImageHeight,
        )

        doc.addImage(clippedCanvas.dataUrl, 'PNG', margin, 0, pdfContentWidth, 0)
        restImageHeight -= pdfHeight
      } else {
        const clippedCanvas = clipCanvas(
          canvas,
          -(heightInCanvasPdf - restImageHeight),
          pdfContentWidth,
          pdfHeight,
        )
        doc.addImage(clippedCanvas.dataUrl, 'PNG', margin, 0, pdfContentWidth, 0)
        doc.addPage()
        restImageHeight -= pdfHeight
      }
    }

    doc.save(`${title}.pdf`)
    if (afterFunc) afterFunc()
    setIsPdfLoading(false)
  }

  const downloadPdf = () => {
    if (beforeFunc) beforeFunc()
    setIsPdfLoading(true)
    setTimeout(() => {
      const input = document.getElementById(target)
      if (!input) return
      generatePdf(input)
    }, 1000)
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isPdfLoading) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isPdfLoading])

  return (
    <div className="download_pdf_area">
      <button
        type="button"
        className="toggle"
        onClick={() => downloadPdf()}
        disabled={isPdfLoading}
      >
        <FontAwesomeIcon icon={['fas', 'file-pdf']} className="icon" />
        PDFデータをダウンロードする
      </button>
      <div className={`close_screen in_message ${isPdfLoading || 'none'}`} role="presentation">
        <LoadingSquare color="#FFFFFF">
          <>
            PDFデータを生成しています…
          </>
        </LoadingSquare>
      </div>
    </div>
  )
}

PdfModule.defaultProps = {
  beforeFunc: undefined,
  afterFunc: undefined,
}

export default PdfModule
